import { useEffect, useState, useCallback, useRef } from 'react';
import { fetchOrderById, supabase, type Order } from '@/lib/supabase';
import CustomerRouteMap from '@/components/feature/CustomerRouteMap';

interface TrackingModalProps {
  orderId: string;
  onClose: () => void;
}

const STEPS = [
  { key: 'pending',   label: 'Order Placed',      icon: 'ri-shopping-bag-line' },
  { key: 'confirmed', label: 'Order Confirmed',    icon: 'ri-checkbox-circle-line' },
  { key: 'shipped',   label: 'Out for Delivery',   icon: 'ri-e-bike-2-fill' },
  { key: 'delivered', label: 'Delivered',          icon: 'ri-home-5-fill' },
];

const STATUS_ORDER = ['pending', 'confirmed', 'shipped', 'delivered'];

export default function TrackingModal({ orderId, onClose }: TrackingModalProps) {
  const [visible, setVisible] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const loadOrder = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    let data = await fetchOrderById(orderId);
    if (!data) data = await fetchOrderById(orderId.toUpperCase());
    if (!data) data = await fetchOrderById(orderId.toLowerCase());
    setOrder(data);
    if (data?.delivery_lat) setLastUpdated(new Date());
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    loadOrder();

    const channel = supabase
      .channel(`order-tracking-${orderId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${orderId}`,
        },
        (payload) => {
          const updated = payload.new as Order;
          setOrder(prev => prev ? { ...prev, ...updated } : updated);
          if (updated.delivery_lat) setLastUpdated(new Date());
        }
      )
      .subscribe();

    channelRef.current = channel;

    const interval = setInterval(() => loadOrder(true), 10000);

    return () => {
      clearInterval(interval);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [loadOrder, orderId]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const currentStatusIdx = order ? STATUS_ORDER.indexOf(order.status) : 0;

  const isLive = !!(order?.delivery_lat && order?.delivery_lng && order?.status === 'shipped');

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center transition-all duration-300 ${visible ? 'bg-black/60' : 'bg-transparent'}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className={`bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-3xl flex flex-col max-h-[92vh] transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full md:translate-y-8'}`}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-300"></div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-gray-900 font-bold text-lg flex items-center gap-2">
              <i className="ri-map-pin-2-line text-orange-500"></i>
              Live Tracking
            </h2>
            <p className="text-gray-500 text-xs mt-0.5">Order ID: {orderId}</p>
          </div>
          <div className="flex items-center gap-2">
            {isLive && (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 border border-green-200 px-2 py-1 rounded-full font-semibold">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>LIVE
              </span>
            )}
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-3">
              <div className="w-8 h-8 rounded-full border-4 border-gray-200 border-t-orange-500 animate-spin"></div>
              <p className="text-gray-400 text-sm">Loading tracking info...</p>
            </div>
          ) : !order ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Order nahi mila</p>
            </div>
          ) : (
            <>
              {/* Status Banner */}
              <div className={`rounded-xl px-4 py-3 flex items-center justify-between border ${
                order.status === 'shipped' ? 'bg-orange-50 border-orange-200' :
                order.status === 'delivered' ? 'bg-green-50 border-green-200' :
                'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    order.status === 'shipped' ? 'bg-orange-100' :
                    order.status === 'delivered' ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    <i className={`text-xl ${
                      order.status === 'shipped' ? 'ri-e-bike-2-fill text-orange-500 animate-pulse' :
                      order.status === 'delivered' ? 'ri-checkbox-circle-fill text-green-500' :
                      'ri-time-line text-amber-500'
                    }`}></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-semibold text-sm">
                      {order.status === 'shipped' ? 'Out for Delivery!' :
                       order.status === 'delivered' ? 'Delivered!' :
                       order.status === 'confirmed' ? 'Order Confirmed' : 'Order Placed'}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {order.status === 'shipped' ? 'Delivery boy raste mein hai' :
                       order.status === 'delivered' ? 'Successfully deliver ho gaya' :
                       'Jaldi deliver hoga'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Live Animated Map */}
              {order.status === 'shipped' && (
                <CustomerRouteMap
                  deliveryLat={order.delivery_lat ?? null}
                  deliveryLng={order.delivery_lng ?? null}
                  customerLat={order.customer_lat ?? null}
                  customerLng={order.customer_lng ?? null}
                  lastUpdated={lastUpdated}
                  deliveryBoyName={order.delivery_boy_name || 'Delivery Boy'}
                  customerAddress={`${order.customer_address || ''}, ${order.customer_city || ''}`}
                  isLive={isLive}
                />
              )}

              {/* Delivery Boy Info */}
              {order.delivery_boy_name && order.status === 'shipped' && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-3">Delivery Partner</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gray-200 flex items-center justify-center shrink-0">
                      <i className="ri-user-3-fill text-gray-500 text-xl"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-semibold text-sm">{order.delivery_boy_name}</p>
                      <p className="text-gray-500 text-xs flex items-center gap-1 mt-0.5">
                        <i className="ri-phone-line"></i>{order.delivery_boy_phone}
                      </p>
                    </div>
                    <a
                      href={`tel:${order.delivery_boy_phone}`}
                      className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center hover:bg-green-200 transition-colors border border-green-200 cursor-pointer"
                    >
                      <i className="ri-phone-fill text-green-600"></i>
                    </a>
                  </div>
                </div>
              )}

              {/* OTP Display */}
              {order.delivery_otp && order.status === 'shipped' && !order.otp_verified && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-shield-keyhole-fill text-amber-500 text-lg"></i>
                    <p className="text-amber-800 font-bold text-sm">Delivery OTP</p>
                  </div>
                  <p className="font-mono font-bold text-amber-900 text-3xl tracking-[0.3em] text-center py-2">
                    {order.delivery_otp}
                  </p>
                  <p className="text-amber-600 text-xs text-center mt-1">
                    Yeh OTP sirf delivery boy ko do — order receive karte waqt
                  </p>
                </div>
              )}

              {/* Order Timeline */}
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <p className="text-gray-900 font-semibold text-sm mb-4">Order Status</p>
                <div className="space-y-0">
                  {STEPS.map((step, idx) => {
                    const stepIdx = STATUS_ORDER.indexOf(step.key);
                    const isCompleted = stepIdx < currentStatusIdx;
                    const isActive = stepIdx === currentStatusIdx;
                    const isFuture = stepIdx > currentStatusIdx;
                    return (
                      <div key={step.key} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            isCompleted ? 'bg-green-500 text-white' :
                            isActive ? 'bg-orange-500 text-white' :
                            'bg-gray-100 text-gray-400'
                          } ${isActive ? 'animate-pulse' : ''}`}>
                            {isCompleted ? (
                              <i className="ri-check-line text-sm"></i>
                            ) : (
                              <i className={`${step.icon} text-sm`}></i>
                            )}
                          </div>
                          {idx < STEPS.length - 1 && (
                            <div className={`w-px flex-1 min-h-[28px] ${isCompleted ? 'bg-green-300' : 'bg-gray-200'}`}></div>
                          )}
                        </div>
                        <div className="pb-5 flex-1">
                          <p className={`text-sm font-semibold ${isFuture ? 'text-gray-400' : 'text-gray-900'}`}>
                            {step.label}
                            {isActive && (
                              <span className="ml-2 text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full">Current</span>
                            )}
                          </p>
                          {isCompleted && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {new Date(order.updated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <p className="text-gray-900 font-semibold text-sm flex items-center gap-2">
                    <i className="ri-shopping-bag-3-line text-orange-500"></i>
                    Order Items ({order.items?.length || 0})
                  </p>
                </div>
                <div className="divide-y divide-gray-100">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="px-4 py-3 flex items-center gap-3">
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className="w-14 h-14 object-cover object-top rounded-lg shrink-0 bg-gray-100"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                          <i className="ri-t-shirt-2-line text-orange-400 text-xl"></i>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 font-medium text-sm truncate">{item.name}</p>
                        <p className="text-gray-500 text-xs mt-0.5">
                          {item.size && <span className="mr-2">Size: {item.size}</span>}
                          Qty: {item.quantity || 1}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-900 font-semibold text-sm">₹{item.price?.toLocaleString('en-IN') || '0'}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">Total Amount</span>
                    <span className="text-gray-900 font-bold text-lg">₹{order.final_amount?.toLocaleString('en-IN') || order.total?.toLocaleString('en-IN') || '0'}</span>
                  </div>
                </div>
              </div>

              {/* Safety Note */}
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 flex items-start gap-2">
                <i className="ri-shield-star-line text-amber-500 mt-0.5 shrink-0"></i>
                <p className="text-gray-600 text-xs leading-relaxed">
                  Delivery partner se order lene se pehle <strong className="text-gray-800">OTP verify karo</strong>. Apna OTP kisi aur ke saath share mat karo.
                </p>
              </div>

              <div className="h-2"></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
